import { useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogCloseButton,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Badge,
	Box,
	Button,
	ChevronRightOutline,
	DesktopComputerOutline,
	DotsHorizontalOutline,
	Flex,
	GlobeAltOutline,
	HStack,
	IconButton,
	Input,
	InputGroup,
	InputLeftElement,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	PencilOutline,
	SearchAltOutline,
	Text,
	Tooltip,
	TrashOutline,
	useDisclosure,
	useToast,
	VStack,
} from '@bonusly/design-system';
import { useIsBonuslyCompany } from '@bonusly/who-am-i';

import {
	useBizyDeleteConversationMutation,
	useBizyShareConversationMutation,
	useBizyUpdateConversationMutation,
	useBizyUserConversations,
} from '../../../queries/bizy-conversations';
import { useIsBizyDesktopDownloadEnabled } from '../hooks/use-bizy-desktop-download';
import { getBizyConversationTitle } from '../lib/conversation-title';
import { removeCachedTitle, setCachedTitle } from '../lib/conversation-title-cache';
import { clearBizySession, loadBizySession } from '../lib/session-storage';
import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';
import { useBizyWidgetStore } from '../store/use-bizy-widget-store';

interface BizyConversationHistoryListProps {
	activeConversationId?: string;
	children?: ReactNode;
	fontSize?: 'sm' | 'md';
	maxW?: string;
	onConversationLoaded: () => void;
	onResetChat: () => void;
	searchHeight?: number;
	searchPb?: number;
	searchPx?: number;
	searchPt?: number;
}

export function BizyConversationHistoryList({
	activeConversationId,
	children,
	fontSize = 'sm',
	maxW,
	onConversationLoaded,
	onResetChat,
	searchHeight,
	searchPb = 3,
	searchPx = 4,
	searchPt = 1.5,
}: BizyConversationHistoryListProps) {
	const { t } = useTranslation('admin_bizy');
	const { track } = useAnalytics();
	const isBonuslyCompany = useIsBonuslyCompany();
	const [searchValue, setSearchValue] = useState('');
	const [renamingId, setRenamingId] = useState<string | undefined>();
	const [renameValue, setRenameValue] = useState('');
	const setResume = useBizyWidgetStore((s) => s.setResume);
	const isDesktopDownloadEnabled = useIsBizyDesktopDownloadEnabled();
	const showSettings = useBizySidePanelStore((s) => s.showSettings);

	const toast = useToast();
	const { data: conversationsData, isLoading } = useBizyUserConversations();
	const { mutate: updateConversation } = useBizyUpdateConversationMutation();
	const { mutate: deleteConversationBackend } = useBizyDeleteConversationMutation();
	const { mutate: shareConversation } = useBizyShareConversationMutation();
	const conversations = conversationsData?.[0] || [];

	const filteredConversations = useMemo(() => {
		if (!searchValue.trim()) return conversations;
		const query = searchValue.toLowerCase();
		return conversations.filter((conversation) => getBizyConversationTitle(conversation).toLowerCase().includes(query));
	}, [conversations, searchValue]);

	const sortedConversations = useMemo(
		() => [...filteredConversations].sort((a, b) => b.lastMessageAt.getTime() - a.lastMessageAt.getTime()),
		[filteredConversations]
	);

	const renameCommittedRef = useRef(false);

	const handleRename = (conversationId: string) => {
		renameCommittedRef.current = false;
		const conversation = conversations.find((c) => c.conversationId === conversationId);
		setRenameValue(conversation ? getBizyConversationTitle(conversation) : '');
		setRenamingId(conversationId);
	};

	const handleFinishRename = () => {
		if (renameCommittedRef.current) return;
		renameCommittedRef.current = true;
		if (renamingId && renameValue.trim()) {
			setCachedTitle(renamingId, renameValue.trim());
			updateConversation({ conversationId: renamingId, title: renameValue.trim() });
			track(AnalyticsEvent.Bizy.ConversationRenamed, { path: window.location.pathname });
		}
		setRenamingId(undefined);
		setRenameValue('');
	};

	const deleteDialog = useDisclosure();
	const deleteCancelRef = useRef<HTMLButtonElement>(null);
	const [deletingConversationId, setDeletingConversationId] = useState<string | undefined>();

	const handleDeleteClick = (conversationId: string) => {
		setDeletingConversationId(conversationId);
		deleteDialog.onOpen();
	};

	const handleShareToggle = (cid: string, shared: boolean) => {
		shareConversation(
			{ conversationId: cid, shared },
			{
				onSuccess: () => {
					toast({
						title: shared ? t('sidePanel.chatSharedSuccess') : t('sidePanel.chatUnsharedSuccess'),
						status: 'success',
						duration: 3000,
					});
				},
				onError: () => {
					toast({ title: t('sidePanel.chatShareError'), status: 'error', duration: 3000 });
				},
			}
		);
	};

	const handleSelectConversation = (conversationId: string) => {
		// Hand only the id to the chat hook and switch to the chat view straight
		// away. The chat hook fetches the detail by id (shared React Query cache,
		// no duplicate request) and shows its own loading state until the data is
		// coherent — so the transition is instant and the wrong-chat desync
		// (BIZY-345) is structurally impossible from here.
		setResume(conversationId);
		onConversationLoaded();
	};

	const handleConfirmDelete = () => {
		if (deletingConversationId) {
			deleteConversationBackend(deletingConversationId, {
				onSuccess: () => {
					removeCachedTitle(deletingConversationId);
					const session = loadBizySession();
					if (session?.conversationId === deletingConversationId) {
						clearBizySession();
						onResetChat();
					}
					setDeletingConversationId(undefined);
					deleteDialog.onClose();
					toast({ title: t('sidePanel.deleteChatSuccess'), status: 'info', duration: 3000 });
					track(AnalyticsEvent.Bizy.ConversationDeleted, { path: window.location.pathname });
				},
				onError: () => {
					setDeletingConversationId(undefined);
					deleteDialog.onClose();
					toast({ title: t('sidePanel.deleteChatError'), status: 'error', duration: 3000 });
				},
			});
		} else {
			setDeletingConversationId(undefined);
			deleteDialog.onClose();
		}
	};

	return (
		<Flex direction="column" h="100%" minH={0}>
			<Box px={searchPx} pt={searchPt} pb={searchPb} maxW={maxW} mx={maxW ? 'auto' : undefined} w="100%">
				<InputGroup size="sm">
					<InputLeftElement pointerEvents="none" h="100%" pl={2}>
						<SearchAltOutline boxSize={4} color="text.softest" />
					</InputLeftElement>
					<Input
						value={searchValue}
						onChange={(event) => setSearchValue(event.target.value)}
						placeholder={t('sidePanel.searchPlaceholder')}
						fontSize={fontSize}
						borderRadius="12px"
						bg="bg.canvas"
						borderColor="border.default"
						py={2.5}
						pr={3}
						h={searchHeight ?? 'auto'}
						letterSpacing={fontSize === 'md' ? 'ui' : undefined}
					/>
				</InputGroup>
			</Box>

			{children}

			<Box flex={1} overflowY="auto" px={3} pb={3}>
				<VStack align="stretch" spacing={0} maxW={maxW} mx={maxW ? 'auto' : undefined}>
					{isLoading && (
						<Text size={fontSize} color="text.soft" px={2} py={2}>
							{t('thinking')}
						</Text>
					)}
					{!isLoading &&
						sortedConversations.map((conversation) => {
							const title = getBizyConversationTitle(conversation) || t('sidePanel.untitledProactiveConversation');
							const isRenaming = renamingId === conversation.conversationId;
							const isActive = activeConversationId === conversation.conversationId;

							return (
								<Flex
									key={conversation.conversationId}
									align="center"
									w="100%"
									px={2.5}
									py={2}
									borderRadius="md"
									bg={isActive ? 'secondary.bgAlpha' : undefined}
									_hover={{ bg: isActive ? 'secondary.bgAlpha' : 'bg.canvas' }}
									role="group"
								>
									<Box
										as={isRenaming ? 'div' : 'button'}
										role={isRenaming ? undefined : 'button'}
										flex={1}
										minW={0}
										textAlign="left"
										cursor={isRenaming ? undefined : 'pointer'}
										onClick={isRenaming ? undefined : () => handleSelectConversation(conversation.conversationId)}
									>
										{isRenaming ? (
											<Input
												value={renameValue}
												onChange={(e) => setRenameValue(e.target.value)}
												onBlur={handleFinishRename}
												onKeyDown={(e) => {
													if (e.key === 'Enter') handleFinishRename();
													if (e.key === 'Escape') {
														renameCommittedRef.current = true;
														setRenamingId(undefined);
														setRenameValue('');
													}
												}}
												size="sm"
												fontSize="md"
												fontWeight="medium"
												variant="outline"
												px={1}
												h={7}
												borderRadius="lg"
												borderColor="primary.fg"
												borderWidth="1.5px"
												_focus={{ borderColor: 'primary.fg', boxShadow: 'none' }}
												autoFocus
												onClick={(e) => e.stopPropagation()}
											/>
										) : (
											<HStack spacing={2} minW={0}>
												<Text size={fontSize} fontWeight={isActive ? 'medium' : 'normal'} color="text.default" noOfLines={1}>
													{title}
												</Text>
												{conversation.sharedWithAdmin && (
													<Badge colorScheme="purple" size="sm" flexShrink={0}>
														{t('sidePanel.sharedBadge')}
													</Badge>
												)}
											</HStack>
										)}
									</Box>
									<Box flexShrink={0} opacity={0} _groupHover={{ opacity: 1 }} _focusWithin={{ opacity: 1 }} transition="opacity 0.1s">
										<Menu>
											<Tooltip label={t('sidePanel.moreActions')} hasArrow={false}>
												<MenuButton
													as={IconButton}
													aria-label={t('sidePanel.moreActions')}
													icon={<DotsHorizontalOutline boxSize={4} />}
													variant="ghost"
													size="xs"
													color="text.softer"
													_hover={{ bg: 'secondary.bgAlphaHover' }}
													onClick={(e: React.MouseEvent) => e.stopPropagation()}
													borderRadius="lg"
												/>
											</Tooltip>
											<MenuList minW="120px" zIndex="popover">
												<MenuItem
													icon={<PencilOutline boxSize={5} />}
													onClick={(e) => {
														e.stopPropagation();
														handleRename(conversation.conversationId);
													}}
												>
													{t('sidePanel.renameChat')}
												</MenuItem>
												{isBonuslyCompany && (
													<MenuItem
														icon={<GlobeAltOutline boxSize={5} />}
														onClick={(e) => {
															e.stopPropagation();
															handleShareToggle(conversation.conversationId, !conversation.sharedWithAdmin);
														}}
													>
														{conversation.sharedWithAdmin ? t('sidePanel.unshareChat') : t('sidePanel.shareChat')}
													</MenuItem>
												)}
												<MenuItem
													color="error.fg"
													icon={<TrashOutline boxSize={5} />}
													onClick={(e) => {
														e.stopPropagation();
														handleDeleteClick(conversation.conversationId);
													}}
												>
													{t('sidePanel.deleteChat')}
												</MenuItem>
											</MenuList>
										</Menu>
									</Box>
								</Flex>
							);
						})}
					{!isLoading && filteredConversations.length === 0 && (
						<Text size={fontSize} color="text.soft" px={2}>
							{t('sidePanel.noHistory')}
						</Text>
					)}
				</VStack>
			</Box>

			{isDesktopDownloadEnabled && (
				<Box borderTop="1px solid" borderColor="border.soft" px={3} py={2} flexShrink={0}>
					<Box maxW={maxW} mx={maxW ? 'auto' : undefined}>
						<Button
							variant="ghost"
							colorScheme="secondary"
							w="100%"
							justifyContent="space-between"
							leftIcon={<DesktopComputerOutline boxSize={5} />}
							rightIcon={<ChevronRightOutline boxSize={4} />}
							onClick={() => showSettings('apps')}
							data-testid="get-bizy-for-desktop"
						>
							<Text as="span" flex={1} textAlign="left" size={fontSize} fontWeight="medium">
								{t('sidePanel.getBizyForDesktop')}
							</Text>
						</Button>
					</Box>
				</Box>
			)}

			<AlertDialog leastDestructiveRef={deleteCancelRef as RefObject<HTMLButtonElement>} isOpen={deleteDialog.isOpen} onClose={deleteDialog.onClose} isCentered>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('sidePanel.deleteChatTitle')}
						</AlertDialogHeader>
						<AlertDialogCloseButton />
						<AlertDialogBody>
							<Text>{t('sidePanel.deleteChatConfirm')}</Text>
						</AlertDialogBody>
						<AlertDialogFooter gap={3}>
							<Button ref={deleteCancelRef} variant="outline" colorScheme="secondary" onClick={deleteDialog.onClose}>
								{t('sidePanel.cancel')}
							</Button>
							<Button variant="solid" colorScheme="danger" onClick={handleConfirmDelete}>
								{t('sidePanel.deleteChat')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Flex>
	);
}
